/**
 * Utility functions for GAP (Glass Processing) detection.
 * 
 * This module provides reusable functions to identify if an order qualifies as GAP
 * based on various conditions. These functions can be used across the entire app
 * for filtering, styling, and conditional logic.
 */

import type { OrderSummary, Order } from './types';

/**
 * Check if an order qualifies as GAP based on the first condition.
 * 
 * First GAP Condition - ALL of the following must be true:
 * 1. Product Type: Must be 'single' OR 'mirror'
 * 2. Glass Value: Must have at least one glass value (arrival or cutting) 
 *    assigned to any glass side
 * 3. Processing Features: Must have at least ONE of:
 *    - Drilled holes (hole_size_mm has value), OR
 *    - Cutout where cutout_shape is 'square' OR 'socket' (NOT 'circle'), OR
 *    - Rounded corners (rounded_corner_radius_mm has value)
 * 
 * @param order - Order instance (OrderSummary or Order type)
 * @returns True if order matches the first GAP condition, False otherwise
 */
export function isOrderGAPConditionOne(order: OrderSummary | Order): boolean {
  // Condition 1: Product type must be 'single' or 'mirror'
  if (order.product_type !== 'single' && order.product_type !== 'mirror') {
    return false;
  }

  // Condition 2: Must have at least one glass value assigned
  let hasGlassValue = false;
  if (order.metadata && typeof order.metadata === 'object') {
    const glassValueIds = [
      order.metadata.glass_side_one_value_id,
      order.metadata.glass_side_two_value_id,
      order.metadata.middle_glass_panel_value_id,
    ];

    // Check if any glass value ID exists
    for (const gvId of glassValueIds) {
      if (gvId) {
        hasGlassValue = true;
        break;
      }
    }
  }

  if (!hasGlassValue) {
    return false;
  }

  // Condition 3: Must have at least one processing feature
  // Check for drilled holes
  const hasDrilledHoles = order.hole_size_mm != null;

  // Check for cutout (only square or socket, NOT circle)
  const hasValidCutout =
    order.cutout_shape != null &&
    (order.cutout_shape === 'square' || order.cutout_shape === 'socket');

  // Check for rounded corners
  const hasRoundedCorners = order.rounded_corner_radius_mm != null;

  // At least one processing feature must be present
  const hasProcessingFeature =
    hasDrilledHoles || hasValidCutout || hasRoundedCorners;

  if (!hasProcessingFeature) {
    return false;
  }

  // All three conditions met
  return true;
}

/**
 * Check if an order qualifies as GAP based on the second condition.
 * 
 * Second GAP Condition - ALL of the following must be true:
 * 1. Product Type: Must be 'single' (only single, nothing else)
 * 2. Glass Value: Must have at least one glass side with glass arrival value 
 *    (is_arrival=True, NOT cutting)
 * 3. Polished: Must have polished edges (polished_edges=True)
 * 
 * Note: This function checks for glass arrival using the *_is_arrival fields
 * that are included in the API response (glass_side_one_is_arrival, etc.).
 * If these fields are not available, it falls back to checking metadata
 * glass value IDs with an optional lookup map.
 * 
 * @param order - Order instance (OrderSummary or Order type)
 * @param glassValuesLookup - Optional lookup map of glass value ID to { is_arrival: boolean, is_cutting: boolean }
 * @returns True if order matches the second GAP condition, False otherwise
 */
export function isOrderGAPConditionTwo(
  order: OrderSummary | Order,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): boolean {
  // Condition 1: Product type must be 'single' (only single)
  if (order.product_type !== 'single') {
    return false;
  }

  // Condition 2: Must have at least one glass arrival value (not cutting)
  if (!orderHasArrivalGlass(order, glassValuesLookup)) {
    return false;
  }

  // Condition 3: Must have polished edges
  // Check for truthy value (handles boolean true, but also handles edge cases)
  if (!order.polished_edges || order.polished_edges === false) {
    return false;
  }

  // All three conditions met
  return true;
}

/**
 * Check if an order has at least one assigned glass arrival value.
 */
function orderHasArrivalGlass(
  order: OrderSummary | Order,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): boolean {
  let hasArrivalGlass =
    (order as any).glass_side_one_is_arrival === true ||
    (order as any).glass_side_two_is_arrival === true ||
    (order as any).middle_glass_panel_is_arrival === true;

  if (!hasArrivalGlass && order.metadata && typeof order.metadata === 'object' && glassValuesLookup) {
    const glassValueIds = [
      order.metadata.glass_side_one_value_id,
      order.metadata.glass_side_two_value_id,
      order.metadata.middle_glass_panel_value_id,
    ];

    for (const gvId of glassValueIds) {
      if (gvId) {
        const glassValue = glassValuesLookup.get(String(gvId));
        if (glassValue?.is_arrival === true) {
          hasArrivalGlass = true;
          break;
        }
      }
    }
  }

  return hasArrivalGlass;
}

/**
 * GA + polished GAP condition - ALL of the following must be true:
 * 1. Product Type: Any
 * 2. Glass Value: At least one assigned glass side with is_arrival=True
 * 3. Polished: polished_edges=True
 *
 * Circle cutouts do not block this condition.
 */
export function isOrderGAPConditionGaPolished(
  order: OrderSummary | Order,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): boolean {
  if (!order.polished_edges || order.polished_edges === false) {
    return false;
  }

  return orderHasArrivalGlass(order, glassValuesLookup);
}

/**
 * Check if an order qualifies as GAP based on the third condition.
 * 
 * Third GAP Condition - ALL of the following must be true:
 * 1. Product Type: Must be 'double' OR 'triple' (either is fine)
 * 2. Glass Value: Must have at least one glass value (arrival or cutting) 
 *    assigned to any glass side (GA or GC, either is fine)
 * 3. Cutout Shape: Must be 'square' OR 'socket' (NOT 'circle')
 * 
 * @param order - Order instance (OrderSummary or Order type)
 * @returns True if order matches the third GAP condition, False otherwise
 */
export function isOrderGAPConditionThree(order: OrderSummary | Order): boolean {
  // Condition 1: Product type must be 'double' or 'triple'
  if (order.product_type !== 'double' && order.product_type !== 'triple') {
    return false;
  }

  // Condition 2: Must have at least one glass value assigned (GA or GC, either is fine)
  let hasGlassValue = false;
  if (order.metadata && typeof order.metadata === 'object') {
    const glassValueIds = [
      order.metadata.glass_side_one_value_id,
      order.metadata.glass_side_two_value_id,
      order.metadata.middle_glass_panel_value_id,
    ];

    // Check if any glass value ID exists (arrival or cutting, either is fine)
    for (const gvId of glassValueIds) {
      if (gvId) {
        hasGlassValue = true;
        break;
      }
    }
  }

  if (!hasGlassValue) {
    return false;
  }

  // Condition 3: Cutout shape must be 'square' OR 'socket' (NOT 'circle')
  if (order.cutout_shape !== 'square' && order.cutout_shape !== 'socket') {
    return false;
  }

  // All three conditions met
  return true;
}

/**
 * Check if an order qualifies as GAP (Glass Processing).
 * 
 * An order is GAP if it matches ANY GAP condition:
 * - GA + Polished: Any product type with arrival glass and polished edges
 *   (including circle cutouts)
 * - Condition One: Single/mirror with glass value and processing features
 * - Condition Two: Single with arrival glass and polished edges
 * - Condition Three: Double/triple with glass value and square cutout
 *
 * IMPORTANT: Orders with circle cutouts are NOT GAP via conditions 1–3,
 * unless the GA + polished condition applies.
 * 
 * @param order - Order instance (OrderSummary or Order type)
 * @param glassValuesLookup - Optional lookup map of glass value ID to { is_arrival: boolean, is_cutting: boolean }
 *                            Used for Condition Two when *_is_arrival fields are not available
 * @returns True if order matches any GAP condition, False otherwise
 */
export function isOrderGAP(
  order: OrderSummary | Order,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): boolean {
  if (isOrderGAPConditionGaPolished(order, glassValuesLookup)) {
    return true;
  }

  if (order.cutout_shape === 'circle') {
    return false;
  }

  return (
    isOrderGAPConditionOne(order) ||
    isOrderGAPConditionTwo(order, glassValuesLookup) ||
    isOrderGAPConditionThree(order)
  );
}

/**
 * Debug helper function to check why an order is or isn't GAP
 * Use this in browser console: window.debugGAPOrder(orderObject)
 * 
 * @param order - Order instance to debug
 * @param glassValuesLookup - Optional lookup map
 * @returns Object with detailed debug information
 */
export function debugGAPOrder(
  order: OrderSummary | Order,
  glassValuesLookup?: Map<string, { is_arrival: boolean; is_cutting: boolean }>
): {
  isGAP: boolean;
  conditionGaPolished: boolean;
  conditionOne: boolean;
  conditionTwo: boolean;
  conditionThree: boolean;
  conditionTwoDetails: {
    productType: string;
    hasArrivalGlass: boolean;
    arrivalGlassSources: string[];
    polishedEdges: any;
    polishedEdgesCheck: boolean;
  };
} {
  const conditionGaPolished = isOrderGAPConditionGaPolished(order, glassValuesLookup);
  const conditionOne = isOrderGAPConditionOne(order);
  const conditionTwo = isOrderGAPConditionTwo(order, glassValuesLookup);
  const conditionThree = isOrderGAPConditionThree(order);
  
  // Debug Condition Two details
  const arrivalGlassSources: string[] = [];
  let hasArrivalGlass = false;
  
  if ((order as any).glass_side_one_is_arrival === true) {
    arrivalGlassSources.push('glass_side_one_is_arrival = true');
    hasArrivalGlass = true;
  }
  if ((order as any).glass_side_two_is_arrival === true) {
    arrivalGlassSources.push('glass_side_two_is_arrival = true');
    hasArrivalGlass = true;
  }
  if ((order as any).middle_glass_panel_is_arrival === true) {
    arrivalGlassSources.push('middle_glass_panel_is_arrival = true');
    hasArrivalGlass = true;
  }
  
  // Check metadata with lookup
  if (!hasArrivalGlass && order.metadata && typeof order.metadata === 'object' && glassValuesLookup) {
    const glassValueIds = [
      { key: 'glass_side_one_value_id', id: order.metadata.glass_side_one_value_id },
      { key: 'glass_side_two_value_id', id: order.metadata.glass_side_two_value_id },
      { key: 'middle_glass_panel_value_id', id: order.metadata.middle_glass_panel_value_id },
    ];
    
    for (const { key, id } of glassValueIds) {
      if (id) {
        const gvIdStr = String(id);
        const glassValue = glassValuesLookup.get(gvIdStr);
        if (glassValue?.is_arrival === true) {
          arrivalGlassSources.push(`${key} = ${id} (found in lookup, is_arrival=true)`);
          hasArrivalGlass = true;
        } else if (glassValue) {
          arrivalGlassSources.push(`${key} = ${id} (found in lookup, is_arrival=false)`);
        } else {
          arrivalGlassSources.push(`${key} = ${id} (NOT found in lookup map)`);
        }
      }
    }
  }
  
  return {
    isGAP: isOrderGAP(order, glassValuesLookup),
    conditionGaPolished,
    conditionOne,
    conditionTwo,
    conditionThree,
    conditionTwoDetails: {
      productType: order.product_type || 'null/undefined',
      hasArrivalGlass,
      arrivalGlassSources: arrivalGlassSources.length > 0 ? arrivalGlassSources : ['No arrival glass found'],
      polishedEdges: order.polished_edges,
      polishedEdgesCheck: !(!order.polished_edges || order.polished_edges === false),
    },
  };
}

// Expose debug function to window for browser console access
if (typeof window !== 'undefined') {
  (window as any).debugGAPOrder = debugGAPOrder;
}

